import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Search, MapPin, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 6 Continents (Antarctica removed)
const continentData = {
  'asia': { color: '#d4a574', name: 'Asia' },
  'europe': { color: '#7a8b99', name: 'Europe' },
  'africa': { color: '#c4956a', name: 'Africa' },
  'north-america': { color: '#6b8e7f', name: 'North America' },
  'south-america': { color: '#8b7355', name: 'South America' },
  'oceania': { color: '#5d8aa8', name: 'Oceania' }
};

const JNTTalesPage = () => {
  const [countries, setCountries] = useState([]);
  const [selectedContinent, setSelectedContinent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [selectedContinent]);

  const fetchCountries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedContinent) {
        const continentName = continentData[selectedContinent]?.name || selectedContinent;
        params.append('continent', continentName);
      }
      
      const res = await axios.get(`${API}/countries?${params.toString()}`);
      setCountries(res.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = countries.filter(country => 
    country.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group countries by continent for display
  const groupedCountries = filteredCountries.reduce((acc, country) => {
    const continent = country.continent || 'Other';
    if (!acc[continent]) acc[continent] = [];
    acc[continent].push(country);
    return acc;
  }, {});

  const getContinentColor = (continent) => {
    const slug = continent?.toLowerCase().replace(/\s+/g, '-');
    return continentData[slug]?.color || '#b8976c';
  };

  return (
    <div className="jnt-tales-page" data-testid="jnt-tales-page">
      {/* Hero Section with Cover Image */}
      <section className="tales-hero">
        <img 
          src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1920" 
          alt="Travel the world" 
          className="tales-hero-bg"
        />
        <div className="tales-hero-overlay"></div>
        <div className="tales-hero-content">
          <h1 className="tales-hero-title">JNT Tales</h1>
          <p className="tales-hero-subtitle">
            Explore destinations by continent, discover countries, and find your next adventure
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="tales-filter-section">
        <div className="tales-container">
          {/* Search Bar */}
          <div className="tales-search-wrapper">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tales-search-input"
              data-testid="tales-search-input"
            />
          </div>

          {/* Continent Filters - 6 continents only */}
          <div className="continent-filters">
            <button
              className={`continent-filter-btn ${!selectedContinent ? 'active' : ''}`}
              onClick={() => setSelectedContinent(null)}
              data-testid="filter-all"
            >
              <Globe size={18} />
              <span>All Continents</span>
            </button>
            {Object.entries(continentData).map(([slug, data]) => (
              <button
                key={slug}
                className={`continent-filter-btn ${selectedContinent === slug ? 'active' : ''}`}
                onClick={() => setSelectedContinent(slug)}
                style={{ '--accent-color': data.color }}
                data-testid={`filter-${slug}`}
              >
                <span>{data.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="tales-posts-section">
        <div className="tales-container">
          {loading ? (
            <div className="tales-loading">
              <div className="loading-spinner"></div>
              <p>Loading destinations...</p>
            </div>
          ) : filteredCountries.length === 0 ? (
            <div className="tales-empty">
              <Globe size={48} />
              <h3>No countries found</h3>
              <p>Check back soon for new destinations.</p>
            </div>
          ) : selectedContinent ? (
            // Single continent view - show countries in grid
            <div className="tales-grid">
              {filteredCountries.map((country) => (
                <Link 
                  to={`/jnt-tales/country/${country.slug}`} 
                  key={country.id} 
                  className="tale-card country-card"
                  data-testid={`country-card-${country.slug}`}
                >
                  <div className="tale-card-image">
                    {country.hero_image ? (
                      <img src={country.hero_image} alt={country.image_alt || country.name} />
                    ) : (
                      <div className="tale-card-placeholder">
                        <MapPin size={32} />
                      </div>
                    )}
                    <div 
                      className="tale-continent-badge"
                      style={{ backgroundColor: getContinentColor(country.continent) }}
                    >
                      {country.continent}
                    </div>
                  </div>
                  <div className="tale-card-content">
                    <h3 className="tale-card-title">{country.name}</h3>
                    <p className="tale-card-intro">{country.description}</p>
                    <div className="tale-card-meta">
                      <span className="read-more">
                        Explore Country <ChevronRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            // All continents view - group by continent
            <div className="continents-grouped">
              {Object.entries(continentData).map(([slug, data]) => {
                const continentCountries = groupedCountries[data.name] || [];
                if (continentCountries.length === 0) return null;
                
                return (
                  <div key={slug} className="continent-section">
                    <div className="continent-header">
                      <h2 style={{ color: data.color }}>{data.name}</h2>
                      <button 
                        className="view-all-btn"
                        onClick={() => setSelectedContinent(slug)}
                      >
                        View All <ChevronRight size={16} />
                      </button>
                    </div>
                    <div className="continent-countries-row">
                      {continentCountries.slice(0, 4).map((country) => (
                        <Link 
                          to={`/jnt-tales/country/${country.slug}`} 
                          key={country.id} 
                          className="country-mini-card"
                          data-testid={`country-mini-${country.slug}`}
                        >
                          <div className="country-mini-image">
                            {country.hero_image ? (
                              <img src={country.hero_image} alt={country.name} />
                            ) : (
                              <div className="country-mini-placeholder">
                                <MapPin size={20} />
                              </div>
                            )}
                          </div>
                          <div className="country-mini-content">
                            <h4>{country.name}</h4>
                            <span>Explore →</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="tales-cta-section">
        <div className="tales-container">
          <div className="tales-cta-content">
            <h2>Ready to Create Your Own Tale?</h2>
            <p>Let us design a personalized journey just for you</p>
            <Link to="/design-journey" className="tales-cta-btn" data-testid="tales-cta-btn">
              Design My Journey
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JNTTalesPage;
